import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import clsx from 'clsx';
import i18next from 'i18next';
import dayjs from 'dayjs';

import { Toast } from 'primereact/toast';
import { Badge } from 'primereact/badge';

import axiosInstance from '../config/axiosConfig';
import useSelectedCompanyStore from '../store/useSelectedCompanyStore';
import usePaginationStore from '../store/usePaginationStore';

import EmptyBoxContent from '../Utils/EmptyBoxContent';
import CandidatesTable from './Tables/CandidatesTable';
import CandidatesInvitationSidebar from '../components/CandidatesInvitationSidebar';

const t = (i18nKey, params) => i18next.t(`userDashboard.candidate.${i18nKey}`, params);

const Candidates = () => {
    const [loading, setLoading] = useState(true);
    const [currentCampaign, setCurrentCampaign] = useState(false);
    const [sidebarVisible, setSidebarVisible] = useState(false);
    const [candidates, setCandidates] = useState([]);

    const toast = useRef(null);

    const history = useHistory();

    const { campaign_id } = useParams();
    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);

    const {
        currentPage,
        limitFilter,
        setLimitFilter,
        totalItems,
        setTotalItems,
        PaginationNavBarQuery,
        setPaginationLoading,
    } = usePaginationStore();

    const fetchData = useCallback(async () => {
        if (!selectedCompany) return;

        setPaginationLoading(true);
        setLoading(true);

        try {
            const response = await axiosInstance.get(`/dashboard/candidates/${selectedCompany.company_id}/${campaign_id}/list`, {
                params: {
                    page: currentPage,
                    limit: limitFilter,
                    query: PaginationNavBarQuery,
                },
            });

            const fetchedCandidates = response.data.items.map((item) => ({
                candidate_id: item.candidate_id,
                username: item.username,
                first_name: item.first_name,
                last_name: item.last_name,
                choosen_tests: item.choosen_tests,
                answers: item.answers,
                email: item.email,
                created_at: dayjs(item.created_at).format('DD/MM/YYYY - HH:mm'),
                fetched_at: dayjs(new Date()).format('DD/MM/YYYY - HH:mm'),
            }));

            setCandidates(fetchedCandidates);
            setCurrentCampaign(response.data.campaign);
            setTotalItems(response.data.count || 0);
        } catch (error) {
            if (error.response && error.response.status === 403) {
                toast.current.show({ severity: 'error', summary: 'Permission insuffisante', detail: "Vous n'avez pas ou plus la permission de voir les participants.", life: 10000 });
                window.location.href = '/dashboard/campaigns/';
            } else {
                toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Erreur lors de la récupération des participants, veuillez réessayer plus tard.', life: 10000 });
            }
        } finally {
            setLoading(false);
            setPaginationLoading(false);
        }
    }, [selectedCompany, campaign_id, currentPage, limitFilter, PaginationNavBarQuery, setTotalItems, setPaginationLoading]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleReloadCandidates = () => {
        setLoading(true);
        fetchData();
    };

    const openSidebar = () => {
        setSidebarVisible(true);
    };

    const closeSidebar = () => {
        setSidebarVisible(false);
    };

    const onPageChange = (event) => {
        setLimitFilter(event.limit);
    };

    return (
        <section className='home'>
            <Toast ref={toast} position={clsx({ 'bottom-right': !sidebarVisible, 'bottom-center': sidebarVisible })} />

            <div className="container">
                <div className="columns is-vcentered is-multiline">
                    <div className="column">
                        <p>
                            <span className="icon has-text-success"><i className="fas fa-check-circle"></i></span>
                            {candidates[candidates?.length - 1]?.applied_at ? <span>{t('updated_at')} {candidates[candidates?.length - 1]?.applied_at} - </span> : <span>{t('updated_at')} {dayjs(Date.now()).format('DD/MM/YYYY - HH:mm')} - </span>}
                            <a className="has-text-link" onClick={handleReloadCandidates}>
                                {t('reload')}
                            </a>
                        </p>
                    </div>

                    <div className="columns">

                        <div className="column has-text-centered">
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                <a className='d-button has-icon-left is-size-6 py-2 m-0 ml-1 no-wrap'
                                    onClick={() => {
                                        const selectedIds = candidates.length ? candidates.map(c => c.candidate_id) : [];
                                        history.push(`/dashboard/campaigns/${campaign_id}/candidates/compare`, { selectedIds });
                                    }}
                                >{t('candidate_comparison')}
                                    <span className="fontawesome-icon-container">
                                        <i className="fas fa-balance-scale"></i>
                                    </span>
                                </a>
                                <Badge value="beta" severity="danger" style={{ position: 'absolute', top: '-10px', right: '-10px' }} />
                            </div>
                        </div>

                        <div className="column has-text-centered">
                            <a className={clsx('d-button is-outlined green-deepsy is-size-6 p-2 m-0 ml-1 no-wrap')}
                                onClick={() => { history.push(`/dashboard/campaigns/${campaign_id}/candidates/edit`); }}
                            >{t('modify_campaign')}
                            </a>
                        </div>

                        <div className="column has-text-centered">
                            <a className={clsx('d-button is-size-6 p-2 m-0 ml-1 no-wrap')} onClick={openSidebar}>{t('invite_candidates')}</a>
                        </div>
                    </div>

                </div>

                {!loading && !candidates.length && (
                    <EmptyBoxContent
                        icon={PaginationNavBarQuery !== '' ? "fa-solid fa-magnifying-glass empty-icon" : "fa-solid fa-users empty-icon"}
                        title={PaginationNavBarQuery !== '' ? "Aucun participant trouvé" : "Vous n'avez pas encore de participant"}
                        content={PaginationNavBarQuery !== '' ? "Essayez d'ajuster votre recherche ou d'inviter de nouveaux participants." : "Afin de gérer vos participants, vous devez d'abord les inviter."}
                        btn1Text="Retour Au Dashboard"
                        btn2Text={t('invite_candidates')}
                        btn1Href="/dashboard/"
                        btn2OnClick={openSidebar}
                    />
                )}

                <CandidatesTable data={candidates} dataCount={candidates.length} dataPages={1} loading={loading} campaign={currentCampaign} />

                <CandidatesInvitationSidebar
                    sidebarVisible={sidebarVisible}
                    closeSidebar={closeSidebar}
                    toast={toast}
                />
            </div>
        </section>
    );
};

export default Candidates;